import { useEffect, useState } from "react";
import html2pdf from "html2pdf.js";
import { apiGetState, apiSetStateKey, apiLogout } from "./api.js";
import { hhConfirm } from "./ConfirmDialog.jsx";
import { esc } from "./escape.js";
import HR_BODY from "./hr-body.js";
import HR_SCRIPT from "./hr-script.js";

// Expose html2pdf on window so the injected legacy HR script can call it.
if (typeof window !== "undefined") {
  window.html2pdf = html2pdf;
}

// localStorage key -> backend state key
export const KEY_MAP = {
  hh3_emps: "emps",
  hh3_att: "att",
  hh3_tds: "tds",
  hh3_kpi: "kpi",
  hh3_pip: "pip",
  hh3_trips: "trips",
  hh3_locks: "locks",
};

const SAVE_DELAY = 300;
const saveTimers = {};

function scheduleSave(key, value) {
  if (saveTimers[key]) clearTimeout(saveTimers[key]);
  saveTimers[key] = setTimeout(() => {
    apiSetStateKey(key, value).catch((e) => {
      console.error("State save failed:", key, e);
    });
  }, SAVE_DELAY);
}

let scriptInjected = false;

/**
 * Pre-process the original HR script:
 *  - Replace `confirm(...)` patterns with hhConfirm
 *  - Wrap write helpers with a lock-check guard
 *  - Append helper globals (monthLocked, hhNotifyLocked)
 */
export function transformScript(src) {
  let out = src;

  // 1) deleteEmp: confirm -> hhConfirm
  out = out.replace(
    /function deleteEmp\(id\)\{[\s\S]*?\}\s*\n/,
    `function deleteEmp(id){
  var emp = emps.find(function(e){return e.id===id;});
  var nm = emp ? emp.name : 'this employee';
  window.hhConfirm('Delete ' + nm + '? This cannot be undone.', function(){
    emps = emps.filter(function(e){return e.id!==id;});
    save('hh3_emps', emps);
    renderEmps();
  }, { danger: true, confirmLabel: 'Delete', title: 'Delete employee' });
}
`,
  );

  // 2) deleteTrip: confirm -> hhConfirm
  out = out.replace(
    /function deleteTrip\(empId,tripId\)\{[\s\S]*?\}\s*\n/,
    `function deleteTrip(empId,tripId){
  window.hhConfirm('Delete this trip record?', function(){
    deleteTripFromState(empId, curYear, curMonth, tripId);
    renderAtt();
  }, { danger: true, confirmLabel: 'Delete trip', title: 'Delete trip' });
}
`,
  );

  // 3) deletePIP: confirm -> hhConfirm
  out = out.replace(
    /function deletePIP\(pipId\)\s*\{[\s\S]*?\}\s*\n/,
    `function deletePIP(pipId){
  window.hhConfirm('Delete this PIP record permanently?', function(){
    pipData = pipData.filter(function(p){return p.id !== pipId;});
    savePIP();
    renderPIP();
  }, { danger: true, confirmLabel: 'Delete PIP', title: 'Delete PIP' });
}
`,
  );

  // 4) Lock-check guard before write helpers
  const lockGuard = `if(window.monthLocked && window.monthLocked(curYear,curMonth)){window.hhNotifyLocked && window.hhNotifyLocked();return;}\n  `;
  out = out.replace(/function setAttS\(empId,d,s\)\{/, `function setAttS(empId,d,s){\n  ${lockGuard}`);
  out = out.replace(/function setVisit\(empId,d,v\)\{/, `function setVisit(empId,d,v){\n  ${lockGuard}`);
  out = out.replace(/function toggleConv\(empId,d\)\{/, `function toggleConv(empId,d){\n  ${lockGuard}`);
  out = out.replace(/function setTds\(k,v\)\{/, `function setTds(k,v){\n  ${lockGuard}`);
  out = out.replace(/function cycleAtt\(empId,d\)\{/, `function cycleAtt(empId,d){\n  ${lockGuard}`);
  out = out.replace(/function markAllPresent\(\)\{/, `function markAllPresent(){\n  ${lockGuard}`);

  // 5) Helper globals at the bottom
  out += `
window.monthLocked = function(y, m){
  try {
    var locks = JSON.parse(localStorage.getItem('hh3_locks') || '{}');
    var key = y + '-' + String(m).padStart(2, '0');
    return !!locks[key];
  } catch(e){ return false; }
};
window.hhNotifyLocked = function(){
  window.hhConfirm && window.hhConfirm('This month is locked. Unlock it from the Salary tab to make changes.', function(){}, { confirmLabel: 'OK', cancelLabel: '', title: 'Month is locked' });
};
`;

  // 6) Gratuity removal — by policy, gratuity provision is no longer
  //    deducted from the salary structure. Set grat = 0 so it neither shrinks
  //    Special Allowance nor appears as a non-zero line on the slip.
  out = out.replace(
    /const grat=Math\.round\(basic\*0\.0481\);[^\n]*/,
    "const grat=0; // Gratuity no longer deducted per company policy",
  );
  // Also hide the "Gratuity prov." / "Gratuity provision" rows entirely from
  // the Salary card and the printable Pay Slip.
  out = out.replace(
    /<div class="sal-row fs"><span style="color:var\(--subtext\);">Gratuity prov\.<\/span><span>\$\{INR\(p\.grat\)\}<\/span><\/div>/g,
    "",
  );
  out = out.replace(
    /<div class="sal-row fs"><span style="color:var\(--subtext\);">Gratuity provision<\/span><span>\$\{INR\(p\.grat\)\}<\/span><\/div>/g,
    "",
  );

  // 6b) Mobile allowance is a reimbursement paid ON TOP of the monthly salary
  //     (CTC). It is no longer part of the Gross or used in Special Allowance
  //     calculation, and it is added to the final Net Pay (not subject to
  //     LOP or statutory deductions).
  out = out.replace(
    /spec=Math\.max\(0,ctc-basic-hra-ta-mobile-erPF-grat\);/,
    "spec=Math.max(0,ctc-basic-hra-ta-erPF-grat); // mobile excluded — paid on top",
  );
  out = out.replace(
    /const gross=basic\+hra\+ta\+mobile\+spec;/,
    "const gross=basic+hra+ta+spec; // mobile is a reimbursement, not part of gross",
  );
  out = out.replace(
    /const netPay=Math\.max\(0,sal\.gross-empPF-pt-empESIC-tds-lopDed\);/,
    "const netPay=Math.max(0,sal.gross-empPF-pt-empESIC-tds-lopDed)+(Number(sal.mobile)||0); // mobile reimbursement added on top",
  );

  // Slip earnings: remove the "Mobile allowance" row from the main earnings
  // table; we'll show it as a reimbursement below.
  out = out.replace(
    /\['Mobile allowance',p\.mobile,''\],\s*\n/,
    "",
  );
  // Salary card earnings: same — remove mobile from earnRows.
  out = out.replace(
    /\['Mobile allowance',p\.mobile\|\|399,''\],\s*\n/,
    "",
  );

  // Slip: after the Gross payable row in tfoot, append a Mobile reimbursement
  // row so the math reads: Gross payable - Total deductions + Mobile = Net pay
  out = out.replace(
    /<tr class="gp-row"><td>Gross payable<\/td><td>\$\{INR\(p\.grossPay\)\}<\/td><\/tr>/,
    `<tr class="gp-row"><td>Gross payable</td><td>\${INR(p.grossPay)}</td></tr>
              \${(Number(p.mobile)||0)>0?\`<tr><td style="color:#1E7C4A;">+ Mobile allowance <span style="font-size:10px;color:var(--subtext);">(reimbursement — on top)</span></td><td style="color:#1E7C4A;">+\${INR(p.mobile)}</td></tr>\`:''}`,
  );

  // Salary card: after the "Gross payable" row, append a small reimbursement
  // info row showing Mobile allowance on top.
  out = out.replace(
    /(<div class="sal-row sal-total" style="color:var\(--navy\);padding-top:6px;border-top:1px solid var\(--border\);margin-top:4px;">\s*\n\s*<span>Gross payable<\/span><span>\$\{INR\(p\.grossPay\)\}<\/span>\s*\n\s*<\/div>)/,
    `$1
          \${(Number(p.mobile)||0)>0?\`<div class="sal-row" style="color:#1E7C4A;margin-top:4px;"><span>+ Mobile allowance <span style="font-size:10px;color:var(--subtext);">(reimbursement)</span></span><span>+\${INR(p.mobile)}</span></div>\`:''}`,
  );

  // Employee modal preview: relabel "Mobile Allow." → "Mobile Allow. (on top)"
  out = out.replace(
    /\['Mobile Allow\.',INR\(s\.mobile\),mob&&mob!=399\?'color:var\(--orange\);':''\],/,
    "['Mobile Allow. (on top)',INR(s.mobile),mob&&mob!=399?'color:var(--orange);':''],",
  );
  // Employee modal preview: Take-Home should include mobile on top
  out = out.replace(
    /\['Take-Home',INR\(s\.gross-s\.empPF-s\.pt-s\.empESIC\),'font-weight:700;color:var\(--navy\);'\],/,
    "['Take-Home',INR(s.gross-s.empPF-s.pt-s.empESIC+s.mobile),'font-weight:700;color:var(--navy);'],",
  );

  // 10) Date format — convert any displayed YYYY-MM-DD to DD-MM-YYYY.
  // Provide a small helper at the top of the script via a prepended line and
  // wrap the known display points with fmtDOJ(...).
  // The injected helper lives on window so existing functions can call it.
  out += `
window.fmtDOJ = function(d){
  if(!d) return '';
  // Accept "YYYY-MM-DD" or any Date-parseable string and emit "DD-MM-YYYY".
  var m = String(d).match(/^(\\d{4})-(\\d{2})-(\\d{2})/);
  if(m) return m[3] + '-' + m[2] + '-' + m[1];
  try {
    var dt = new Date(d);
    if(!isNaN(dt.getTime())){
      var dd = String(dt.getDate()).padStart(2,'0');
      var mm = String(dt.getMonth()+1).padStart(2,'0');
      return dd + '-' + mm + '-' + dt.getFullYear();
    }
  } catch(e) {}
  return String(d);
};
`;

  // Slip "Date of joining" — wrap emp.doj with the formatter
  out = out.replace(
    /\['Date of joining',emp\.doj\]/,
    "['Date of joining',window.fmtDOJ(emp.doj)]",
  );

  // PIP period and initiated lines
  out = out.replace(
    /PIP Period: \$\{pip\.startDate\} → \$\{pip\.endDate\} \((\$\{total\}) days\) · Initiated: \$\{pip\.startDate\}/,
    "PIP Period: ${window.fmtDOJ(pip.startDate)} → ${window.fmtDOJ(pip.endDate)} ($1 days) · Initiated: ${window.fmtDOJ(pip.startDate)}",
  );

  // 11) MUSTER fix — daily conveyance was hard-coded ₹250/₹125. Use per-
  //     employee dailyConv and the 3-state multiplier instead.
  out = out.replace(
    /const convOn = canClaim && att\[`\$\{emp\.id\}_\$\{y\}_\$\{m\}_\$\{d\}_conv`\] !== 0;\s*\n\s*const convAmt = convOn \? \(s === 'P' \? 250 : 125\) : 0;/,
    `var _convMulRaw=att[\`\${emp.id}_\${y}_\${m}_\${d}_conv\`];
    var _convMul=(_convMulRaw===undefined||_convMulRaw===null)?1:Number(_convMulRaw);
    if(isNaN(_convMul))_convMul=1;
    var _baseConv=Number(emp.dailyConv)||250;
    var _statusBase=s==='HD'?_baseConv/2:_baseConv;
    var convAmt = canClaim ? Math.round(_statusBase * _convMul) : 0;
    var convOn = canClaim && _convMul > 0;`,
  );

  // 12) TRIP MODAL — Replace the Lodging/Conveyance/Other fixed-fields with a
  //     dynamic expense list. Show policy caps clearly and a running total
  //     with within/exceeds status.
  // The replacement spans from the "Lodging & Expenses" section header through
  // the closing tag of the actual-total form-row (just before <!-- Summary -->).
  out = out.replace(
    /<div class="form-section-title"><i class="ti ti-bed"[\s\S]*?id="t-actual-total"[\s\S]*?<\/div>\s*<\/div>/,
    `<div class="form-section-title"><i class="ti ti-receipt-2" style="font-size:12px;"></i> Expenses claimed</div>
        <div id="trip-expenses-list" style="margin-bottom:8px;"></div>
        <button type="button" class="btn btn-sm" onclick="addTripExpense()" data-testid="add-trip-expense-btn" style="margin-bottom:4px;">
          <i class="ti ti-plus"></i> Add expense
        </button>
        <div style="font-size:10px;color:var(--subtext);margin-top:2px;">
          Add any expense type (Lodging, Food, Local conveyance, Tolls, etc.) with the amount you spent. Add as many lines as needed.
        </div>`,
  );

  // Add a clarifying note for the city input + datalist.
  out = out.replace(
    /<input id="t-city" list="city-list" placeholder="Type city name…" oninput="cityAutoFill\(\)">/,
    `<input id="t-city" list="city-list" placeholder="Type city name…" oninput="cityAutoFill()">
            <div style="font-size:10px;color:var(--subtext);margin-top:3px;">
              Type any city. Known cities auto-fill Zone &amp; Class — otherwise select them manually below.
            </div>`,
  );

  // Replace the Summary block (CLAIMED / POLICY CAPS two-column) with a
  // single, cleaner "Total reimbursement + status" panel.
  out = out.replace(
    /<!-- Summary -->\s*<div id="trip-summary"[\s\S]*?id="sum-status"[\s\S]*?<\/div>\s*<\/div>/,
    `<!-- Summary -->
        <div id="trip-summary" style="background:var(--lnavy);border-radius:10px;padding:14px 16px;margin-top:4px;">
          <div id="trip-policy-detail" style="font-size:11px;color:var(--subtext);margin-bottom:10px;line-height:1.6;"></div>
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
            <div style="font-size:13px;font-weight:700;color:var(--navy);">
              <i class="ti ti-calculator"></i> Total reimbursement: <span id="trip-total-claimed" style="color:var(--orange);font-size:16px;">₹0</span>
            </div>
            <div id="trip-policy-status" style="font-size:12px;font-weight:600;padding:5px 11px;border-radius:8px;">—</div>
          </div>
          <div id="trip-policy-cap-line" style="font-size:11px;color:var(--subtext);margin-top:6px;"></div>
        </div>`,
  );

  // 13) MUSTER outstation trip row — handle the new expense-list structure
  //     (t.policyCap, t.expenses) and gracefully fall back for legacy trips.
  out = out.replace(
    /const policyTotal = \(t\.lodgingMode==='flat'\?cap\.flat:cap\.actual\)\*\(t\.nights\|\|0\) \+ \(t\.conveyanceDays\|\|0\)\*250 \+ \(t\.resConv\|\|0\);/,
    `const policyTotal = (t.policyCap != null)
      ? t.policyCap
      : (t.lodgingMode==='flat'?cap.flat:cap.actual)*(t.nights||0) + (t.conveyanceDays||0)*250 + (t.resConv||0);`,
  );
  // Replace the Lodging cell with an expense summary when expenses[] is present.
  out = out.replace(
    /<td style="text-align:center;">\$\{t\.lodgingMode==='flat'\?'Flat':'Actuals'\}<\/td>/,
    `<td style="text-align:center;font-size:9px;">\${(t.expenses && t.expenses.length) ? (t.expenses.length + ' item' + (t.expenses.length===1?'':'s')) : (t.lodgingMode === 'flat' ? 'Flat' : (t.lodgingMode === 'actual' ? 'Actuals' : '—'))}</td>`,
  );

  // Inject helper functions for the new expense system + override calcTripPolicy
  // and saveTrip to use the expense list. Appended at the bottom of the
  // transformed script so they override the originals.
  out += `
// ── Trip expense list (custom expenses model) ─────────────────────────
window.__tripExpenses = [];
window.addTripExpense = function(prefillType, prefillAmt){
  window.__tripExpenses.push({ type: prefillType || '', amount: prefillAmt || 0 });
  renderTripExpenses();
};
window.removeTripExpense = function(idx){
  window.__tripExpenses.splice(idx, 1);
  renderTripExpenses();
};
window.updateTripExpense = function(idx, field, val){
  if(!window.__tripExpenses[idx]) return;
  if(field === 'amount') val = Number(val) || 0;
  window.__tripExpenses[idx][field] = val;
  // Just update totals — avoid full re-render so inputs keep focus.
  calcTripPolicy();
};
window.renderTripExpenses = function(){
  var list = document.getElementById('trip-expenses-list');
  if(!list) return;
  if(window.__tripExpenses.length === 0){
    list.innerHTML = '<div style="color:#888;font-size:11px;padding:8px 0;font-style:italic;">No expenses added yet. Click "+ Add expense" below to add a line.</div>';
  } else {
    list.innerHTML = window.__tripExpenses.map(function(e, i){
      var typeVal = (e.type || '').replace(/"/g, '&quot;');
      return '<div style="display:grid;grid-template-columns:1fr 140px 36px;gap:6px;margin-bottom:6px;align-items:center;" data-testid="trip-expense-row-' + i + '">' +
        '<input type="text" placeholder="Expense type (e.g. Lodging, Food, Auto fare)" value="' + typeVal + '" oninput="updateTripExpense(' + i + ', \\'type\\', this.value)" data-testid="trip-expense-type-' + i + '" />' +
        '<input type="number" min="0" placeholder="Amount ₹" value="' + (Number(e.amount) || '') + '" oninput="updateTripExpense(' + i + ', \\'amount\\', this.value)" data-testid="trip-expense-amount-' + i + '" />' +
        '<button type="button" class="btn btn-sm btn-del" onclick="removeTripExpense(' + i + ')" data-testid="trip-expense-remove-' + i + '" title="Remove this expense"><i class="ti ti-trash"></i></button>' +
        '</div>';
    }).join('');
  }
  calcTripPolicy();
};

// Override calcTripPolicy to drive the new total + status from the expense list
window.calcTripPolicy = function(){
  var empId = document.getElementById('t-emp')?.value;
  var emp = emps.find(function(e){ return e.id === empId; });
  if(!emp) return;
  var cls = document.getElementById('t-class')?.value || 'C';
  var nights = Number(document.getElementById('t-nights')?.value || 0);
  var cap = getCap(emp.grade, cls);
  var grp = grpGrade(emp.grade);

  // Policy bar (still uses existing UI)
  var cpGrade = document.getElementById('cp-grade');
  var cpFlat = document.getElementById('cp-flat');
  var cpActual = document.getElementById('cp-actual');
  var cpInc = document.getElementById('cp-inc');
  if(cpGrade) cpGrade.textContent = grp;
  if(cpFlat) cpFlat.textContent = INR(cap.flat);
  if(cpActual) cpActual.textContent = INR(cap.actual);
  if(cpInc) cpInc.textContent = cap.inc > 0 ? INR(cap.inc) : (grp === 'JM5+' ? 'Nil' : INR(cap.inc));

  // Build expense total
  var total = (window.__tripExpenses || []).reduce(function(s, e){
    return s + (Number(e.amount) || 0);
  }, 0);
  var tot = document.getElementById('trip-total-claimed');
  if(tot) tot.textContent = INR(total);

  // Policy cap (use the higher of flat vs actuals — actuals if bills attached)
  var lodgingCap = cap.actual * nights;
  var incidentalCap = cap.inc * nights;
  var policyCap = lodgingCap + incidentalCap;
  // Add a reasonable conveyance allowance for the trip: \${dailyConv}/day for nights+1
  var convAllow = (Number(emp.dailyConv) || 250) * (nights + 1);
  policyCap += convAllow;

  var detail = document.getElementById('trip-policy-detail');
  if(detail){
    detail.innerHTML =
      '<b style="color:var(--navy);">Policy for ' + grp + ' / Class ' + cls + ' (' + nights + ' nights):</b><br/>' +
      '• Lodging actuals (with bills): up to ' + INR(cap.actual) + '/night → max ' + INR(lodgingCap) + '<br/>' +
      '• Lodging flat (no bills): ' + INR(cap.flat) + '/night<br/>' +
      (cap.inc > 0 ? '• Incidental: ' + INR(cap.inc) + '/night → max ' + INR(incidentalCap) + '<br/>' : '') +
      '• In-market conveyance: ' + INR(Number(emp.dailyConv) || 250) + '/day';
  }

  var capLine = document.getElementById('trip-policy-cap-line');
  if(capLine){
    capLine.innerHTML = 'Approx. policy ceiling for this trip: <b style="color:var(--navy);">' + INR(policyCap) + '</b> (lodging + incidental + conveyance)';
  }

  var status = document.getElementById('trip-policy-status');
  if(status){
    if(total === 0){
      status.textContent = 'No expenses yet';
      status.style.background = '#eef3fa';
      status.style.color = '#1A3C6E';
    } else if(total <= policyCap){
      status.innerHTML = '<i class="ti ti-circle-check"></i> Within allowable limit';
      status.style.background = '#dcfce7';
      status.style.color = '#166534';
    } else {
      status.innerHTML = '<i class="ti ti-alert-triangle"></i> Exceeds allowable limit · subject to approval';
      status.style.background = '#fef3c7';
      status.style.color = '#92400e';
    }
  }
};

// Override saveTrip to persist the expense list
window.saveTrip = function(){
  var empId = document.getElementById('t-emp')?.value;
  var emp = emps.find(function(e){ return e.id === empId; });
  var from = document.getElementById('t-from')?.value;
  var to = document.getElementById('t-to')?.value;
  var city = document.getElementById('t-city')?.value?.trim();
  if(!emp || !from || !to || !city){ alert('Employee, dates and city are required.'); return; }
  var cls = document.getElementById('t-class')?.value || 'C';
  var nights = Number(document.getElementById('t-nights')?.value || 0);
  var expenses = (window.__tripExpenses || []).filter(function(e){
    return (e.type && e.type.trim()) || Number(e.amount) > 0;
  });
  var totalClaimed = expenses.reduce(function(s, e){ return s + (Number(e.amount) || 0); }, 0);

  var cap = getCap(emp.grade, cls);
  var policyCap = cap.actual * nights + cap.inc * nights + (Number(emp.dailyConv) || 250) * (nights + 1);

  var trip = {
    fromDate: from, toDate: to, city: city,
    zone: document.getElementById('t-zone')?.value || '',
    cityClass: cls,
    travelMode: document.getElementById('t-mode')?.value || '',
    nights: nights,
    expenses: expenses,
    totalClaimed: totalClaimed,
    policyCap: policyCap,
    exceedsPolicy: totalClaimed > policyCap,
  };
  addTripToState(empId, curYear, curMonth, trip);
  closeTripModal();
  renderAtt();
};

// Reset expense list when modal opens; pre-fill from edit if needed.
var __origOpenTripModal = window.openTripModal;
window.openTripModal = function(){
  window.__tripExpenses = [];
  __origOpenTripModal && __origOpenTripModal();
  setTimeout(function(){ renderTripExpenses(); }, 30);
};
`;

  // 7) Daily conveyance is now per-employee (emp.dailyConv, default 250) and
  //    cycles through 3 states on click: full → half → zero → full → ...
  //    Storage: att[..._d_conv] holds the multiplier (1, 0.5, or 0). Missing
  //    value means full (1).
  out = out.replace(
    /function calcConvTotal\(emp,y,m\)\{[\s\S]*?\n\}/,
    `function calcConvTotal(emp,y,m){
  var total=0;
  var days2=daysIn(y,m);
  var base=Number(emp.dailyConv)||250;
  for(var d=1;d<=days2;d++){
    var s=att[emp.id+'_'+y+'_'+m+'_'+d];
    if(s==='P'||s==='HD'){
      var storedM=att[emp.id+'_'+y+'_'+m+'_'+d+'_conv'];
      var mult=(storedM===undefined||storedM===null)?1:Number(storedM);
      if(isNaN(mult))mult=1;
      var per=(s==='HD'?base/2:base)*mult;
      total+=Math.round(per);
    }
  }
  return total;
}`,
  );

  // Replace toggleConv with a 3-state cycle.
  out = out.replace(
    /function toggleConv\(empId,d\)\{[\s\S]*?\n\}/,
    `function toggleConv(empId,d){
  if(window.monthLocked && window.monthLocked(curYear,curMonth)){window.hhNotifyLocked && window.hhNotifyLocked();return;}
  var k=empId+'_'+curYear+'_'+curMonth+'_'+d+'_conv';
  var stored=att[k];
  var curr=(stored===undefined||stored===null)?1:Number(stored);
  if(isNaN(curr))curr=1;
  // Cycle: 1 (full) -> 0.5 (half) -> 0 (zero) -> 1
  var next;
  if(curr>=1)next=0.5;
  else if(curr>0)next=0;
  else next=1;
  att[k]=next;
  save('hh3_att',att);
  renderAtt();
}`,
  );

  // Patch the inline conveyance calc inside renderAtt: replace the fixed
  // ₹250/₹125 with the multiplier-based per-employee amount.
  out = out.replace(
    /const convOn=canClaim&&att\[convKey\]!==0; \/\/ default ON for P\/HD\s*\n\s*const convAmt=s==='P'\?250:s==='HD'\?125:0;\s*\n\s*if\(convOn&&canClaim\)totalConv\+=convAmt;/,
    `var _dailyBase=Number(emp.dailyConv)||250;
      var _storedM=att[convKey];
      var convMult=(_storedM===undefined||_storedM===null)?1:Number(_storedM);
      if(isNaN(convMult))convMult=1;
      var _baseForStatus=s==='HD'?_dailyBase/2:_dailyBase;
      var convAmt=canClaim?Math.round(_baseForStatus*convMult):0;
      var convOn=canClaim&&convMult>0;
      var convClass=convMult>=1?'conv-on':(convMult>0?'conv-half':'conv-off');
      if(canClaim)totalConv+=convAmt;`,
  );

  // Patch the convBtn html line to use the new convClass + show ₹0 when zero.
  out = out.replace(
    /const convBtn=canClaim\?`<button class="conv-btn \$\{convOn\?'conv-on':'conv-off'\}" onclick="toggleConv\('\$\{emp\.id\}',\$\{d\}\)" title="Daily conveyance ₹\$\{convAmt\} — click to toggle">\$\{convOn\?'₹'\+convAmt:'—'\}<\/button>`:/,
    "const convBtn=canClaim?`<button class=\"conv-btn ${convClass}\" onclick=\"toggleConv('${emp.id}',${d})\" title=\"Daily conveyance — click to cycle full / half / zero\">₹${convAmt}</button>`:",
  );

  // Update the legend to remove the hardcoded ₹250 and reflect the new cycle.
  out = out.replace(
    /<div class="legend-item" style="gap:4px;"><div class="conv-btn conv-on" style="cursor:default;">₹250<\/div>= Daily conveyance claimed<\/div>\s*<div class="legend-item"><div class="conv-btn conv-off" style="cursor:default;">—<\/div>= Not claimed \/ transit<\/div>\s*<span style="font-size:11px;color:#aaa;margin-left:4px;">Click status to cycle · Enter number for visits · Click ₹ to toggle conveyance<\/span>/,
    `<div class="legend-item" style="gap:4px;"><div class="conv-btn conv-on" style="cursor:default;">₹full</div>= Full daily conveyance</div>
      <div class="legend-item" style="gap:4px;"><div class="conv-btn conv-half" style="cursor:default;">½</div>= Half day claim</div>
      <div class="legend-item"><div class="conv-btn conv-off" style="cursor:default;">₹0</div>= Not claimed</div>
      <span style="font-size:11px;color:#aaa;margin-left:4px;">Click status to cycle · Enter visits · Click ₹ to cycle full/half/zero · Rate per employee (set in profile)</span>`,
  );

  // Patch saveEmployee: include dailyConv in the saved record.
  out = out.replace(
    /(visitTarget:Number\(document\.getElementById\('f-visit-target'\)\.value\|\|12\)\|\|12,\s*\n\s*\};)/,
    `visitTarget:Number(document.getElementById('f-visit-target').value||12)||12,
    dailyConv:Number(document.getElementById('f-dailyConv')?.value||250)||250,
  };`,
  );

  // Patch editEmployee: populate f-dailyConv when opening the edit modal.
  out = out.replace(
    /(document\.getElementById\('f-mobile'\)\.value=emp\.mobile\|\|399;)/,
    `$1
  if(document.getElementById('f-dailyConv'))document.getElementById('f-dailyConv').value=emp.dailyConv||250;`,
  );

  // Also reset dailyConv when "Add employee" is clicked. openAdd() uses a
  // loop over field names so we patch the loop list and add an explicit
  // setter for f-dailyConv.
  out = out.replace(
    /(document\.getElementById\('modal-title'\)\.textContent='Add New Employee';)/,
    `$1
  if(document.getElementById('f-dailyConv'))document.getElementById('f-dailyConv').value=250;`,
  );

  // 8) Brand logo — replace plain "HAPPY HANDS GLOBAL LLP" headings in the
  //    PDF muster and salary slip with the actual logo image alongside the
  //    company name.
  // Absolute origin is captured at runtime so the popup PDF can load the
  // image from the same host.
  out = out.replace(
    /<h1>HAPPY HANDS GLOBAL LLP<\/h1>/g,
    `<div style="display:flex;align-items:center;justify-content:center;gap:14px;margin:0 0 6px;">
       <img src="\${(typeof window!=='undefined'&&window.location?window.location.origin:'')}/logo-hh.png" alt="Happy Hands" style="height:54px;width:auto;background:#fff;border-radius:8px;padding:2px;">
       <h1 style="margin:0;">HAPPY HANDS GLOBAL LLP</h1>
     </div>`,
  );

  // Salary slip header: prepend a logo above the company name block.
  out = out.replace(
    /<div class="slip-co">HAPPY HANDS GLOBAL LLP<\/div>/,
    `<div style="display:flex;align-items:center;justify-content:center;gap:14px;margin-bottom:6px;">
        <img src="/logo-hh.png" alt="Happy Hands" style="height:52px;width:auto;background:#fff;border-radius:8px;padding:3px;">
        <div class="slip-co" style="margin:0;">HAPPY HANDS GLOBAL LLP</div>
      </div>`,
  );

  // 9) Salary slip PDF: replace the browser-print button with a clean
  //    html2pdf download (no browser headers/footers, proper filename).
  out = out.replace(
    /<button class="btn btn-primary" onclick="window\.print\(\)"><i class="ti ti-printer"><\/i> Print \/ Download PDF<\/button>/,
    `<button class="btn btn-primary" onclick="downloadSlipPDF()"><i class="ti ti-download"></i> Download PDF</button>
       <button class="btn" onclick="window.print()" title="Open browser print dialog" style="margin-left:6px;"><i class="ti ti-printer"></i> Print</button>`,
  );

  // Append the downloadSlipPDF helper to the bottom of the script.
  out += `
window.downloadSlipPDF = function(){
  try {
    var slip = document.querySelector('#slip-page .slip');
    if(!slip){ window.print(); return; }
    var emp = emps.find(function(e){return e.id===slipEmpId;}) || emps[0];
    var monthName = ['January','February','March','April','May','June','July','August','September','October','November','December'][curMonth-1] || '';
    var fname = 'Salary_Slip_' + (emp ? emp.name.replace(/\\s+/g,'_') : 'Employee') + '_' + monthName + '_' + curYear + '.pdf';
    var btn = document.querySelector('#slip-page .btn.btn-primary');
    var btnOrig = btn ? btn.innerHTML : null;
    if(btn){ btn.disabled = true; btn.innerHTML = '<i class="ti ti-loader-2"></i> Generating PDF…'; }
    var opts = {
      margin: [8, 8, 8, 8],
      filename: fname,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };
    if(typeof window.html2pdf === 'undefined'){
      console.error('html2pdf not loaded, falling back to print');
      if(btn && btnOrig){ btn.disabled = false; btn.innerHTML = btnOrig; }
      window.print();
      return;
    }
    window.html2pdf().set(opts).from(slip).save().then(function(){
      if(btn && btnOrig){ btn.disabled = false; btn.innerHTML = btnOrig; }
    }).catch(function(err){
      console.error('PDF generation failed:', err);
      if(btn && btnOrig){ btn.disabled = false; btn.innerHTML = btnOrig; }
      window.print();
    });
  } catch(e){
    console.error('downloadSlipPDF error:', e);
    window.print();
  }
};
`;

  return out;
}

const HR_SCRIPT_TRANSFORMED = transformScript(HR_SCRIPT);

/**
 * Bootstrap the HR application:
 *  1. Fetch state from backend
 *  2. Pre-seed localStorage with backend values
 *  3. Monkey-patch localStorage.setItem to debounce-save to backend
 *  4. Inject HR_BODY into the mount node + a user/logout chip into the header
 *  5. Tag tabs with data-tab attributes + hide tabs by role
 *  6. Inject the transformed HR_SCRIPT (once)
 *  7. Set default active tab
 */
export default function useHRBootstrap({ user, mountRef, onLogout, onUsersClick }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);

  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const isEmployee = role === "employee";

  useEffect(() => {
    let cancelled = false;

    async function seedLocalStorage(state) {
      Object.entries(KEY_MAP).forEach(([lsKey, beKey]) => {
        const val = state[beKey];
        const fallback = lsKey === "hh3_pip" ? [] : {};
        window.localStorage.setItem(
          lsKey,
          JSON.stringify(val == null ? fallback : val),
        );
      });
    }

    function patchLocalStorage() {
      if (window.__hh3_setItem_patched) return;
      const origSet = window.localStorage.setItem.bind(window.localStorage);
      window.localStorage.setItem = (k, v) => {
        origSet(k, v);
        if (!KEY_MAP[k]) return;
        // Employee role is read-only
        if (isEmployee) return;
        try {
          scheduleSave(KEY_MAP[k], JSON.parse(v));
        } catch (e) {
          console.error("Failed to schedule save for", k, e);
        }
      };
      window.__hh3_setItem_patched = true;
    }

    function injectUserBar() {
      const headerInner = document.getElementById("header-inner");
      if (!headerInner || document.getElementById("hh-userbar")) return;

      // Replace the "HH" text in the orange logo-box with the actual logo image.
      const logoBox = headerInner.querySelector(".logo-box");
      if (logoBox && !logoBox.querySelector("img")) {
        logoBox.textContent = "";
        const img = document.createElement("img");
        img.src = "/logo-hh.png";
        img.alt = "Happy Hands";
        logoBox.appendChild(img);
      }

      const right = headerInner.querySelector(":scope > div:last-child");
      if (!right) return;
      const bar = document.createElement("div");
      bar.id = "hh-userbar";
      bar.style.cssText =
        "display:flex;align-items:center;gap:8px;margin-left:14px;";
      const roleColors = {
        admin: "#e8611a",
        manager: "#2E6DA4",
        employee: "#1E7C4A",
      };
      const usersBtnHtml = isAdmin
        ? `<button id="hh-users-trigger" data-testid="users-btn" title="User Management" style="background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.25);padding:5px 10px;border-radius:8px;font-size:11px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:4px;font-family:inherit;"><i class="ti ti-users"></i> Users</button>`
        : "";
      bar.innerHTML = `
        <span data-testid="user-role-badge" style="font-size:10px;color:#fff;background:${roleColors[role] || "#666"};padding:2px 8px;border-radius:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;">${esc(role)}</span>
        <span data-testid="user-email" style="font-size:11px;color:rgba(255,255,255,0.85);background:rgba(255,255,255,0.1);padding:4px 10px;border-radius:12px;white-space:nowrap;">${esc(user?.email || "")}</span>
        ${usersBtnHtml}
        <button id="hh-logout-trigger" data-testid="logout-btn" style="background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.25);padding:5px 12px;border-radius:8px;font-size:11px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:4px;font-family:inherit;">
          <i class="ti ti-logout"></i> Logout
        </button>`;
      right.appendChild(bar);
      const logoutBtn = document.getElementById("hh-logout-trigger");
      if (logoutBtn) logoutBtn.addEventListener("click", handleLogout);
      const usersBtn = document.getElementById("hh-users-trigger");
      if (usersBtn) usersBtn.addEventListener("click", () => onUsersClick && onUsersClick());
    }

    function tagTabsAndHideByRole() {
      const tabs = ["dashboard", "employees", "attendance", "salary", "slip", "kpi", "pip"];
      document.querySelectorAll(".tabs .tab").forEach((el, i) => {
        if (tabs[i]) el.setAttribute("data-tab", tabs[i]);
      });
      if (isEmployee) {
        document
          .querySelector('.tab[data-tab="employees"]')
          ?.style.setProperty("display", "none");
        document
          .querySelector('.tab[data-tab="pip"]')
          ?.style.setProperty("display", "none");
      }
    }

    function injectDailyConvField() {
      // Add a "Daily Conveyance Allowance" input next to f-mobile in the
      // Add/Edit Employee modal. The original form HTML doesn't have it.
      const mobileInput = document.getElementById("f-mobile");
      if (!mobileInput || document.getElementById("f-dailyConv")) return;
      const mobileGroup = mobileInput.closest(".form-group");
      const formRow = mobileGroup?.parentElement;
      if (!formRow) return;
      // Append a new form-row below with the daily-conveyance field.
      const newRow = document.createElement("div");
      newRow.className = "form-row";
      newRow.innerHTML = `
        <div class="form-group">
          <label>Daily Conveyance (₹/day) <span class="info-tag">Editable</span></label>
          <input id="f-dailyConv" type="number" min="0" placeholder="Default: 250" value="250">
          <div style="font-size:10px;color:var(--subtext);margin-top:3px;">
            Per-day conveyance amount claimed for each Present day (e.g., ₹250 for JM1–JM2, ₹350 for senior roles).
          </div>
        </div>
      `;
      formRow.parentElement.insertBefore(newRow, formRow.nextSibling);
    }

    function injectScript() {
      if (scriptInjected) return;
      const tag = document.createElement("script");
      tag.id = "hr-original-script";
      tag.text = HR_SCRIPT_TRANSFORMED;
      document.body.appendChild(tag);
      scriptInjected = true;
    }

    async function bootstrap() {
      try {
        const state = await apiGetState();
        if (cancelled) return;

        await seedLocalStorage(state);
        setEmployees(state.emps || []);
        patchLocalStorage();
        window.hhConfirm = window.hhConfirm || hhConfirm;

        if (mountRef.current) {
          mountRef.current.innerHTML = HR_BODY;
        }

        try {
          injectUserBar();
        } catch (e) {
          console.error("injectUserBar failed:", e);
        }
        try {
          tagTabsAndHideByRole();
        } catch (e) {
          console.error("tagTabsAndHideByRole failed:", e);
        }
        try {
          injectDailyConvField();
        } catch (e) {
          console.error("injectDailyConvField failed:", e);
        }

        injectScript();

        // Set default active tab once the script has had a chance to define setTab
        setTimeout(() => {
          try {
            if (typeof window.setTab === "function") {
              window.setTab(isEmployee ? "salary" : "dashboard");
            }
          } catch (e) {
            console.error("Setting default tab failed:", e);
          }
        }, 50);

        setLoading(false);
      } catch (e) {
        console.error("HR app bootstrap failed:", e);
        if (e?.response?.status === 401) {
          onLogout && onLogout();
          return;
        }
        setError(e?.message || "Failed to load data");
        setLoading(false);
      }
    }

    async function handleLogout() {
      try {
        await apiLogout();
      } catch (e) {
        console.warn("Logout API call failed:", e);
      }
      Object.keys(KEY_MAP).forEach((k) => window.localStorage.removeItem(k));
      onLogout && onLogout();
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
    // This effect intentionally runs once per HRApp mount. The values it
    // captures (user, role) come from the parent and a remount happens on
    // login/logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading, error, employees, isAdmin, isEmployee };
}
